import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { UploadSimple, FloppyDisk, Trash, Plus, ArrowClockwise } from '@phosphor-icons/react';

const initialState = {
  company_name: '', tagline: '', address: '', phones: [], email: '', gstin: '',
  iso_certification: '', iso_cert_number: '',
  logo_url: '', primary_color: '#F37B21', secondary_color: '#4A4A4A',
  bank: { bank_name: '', branch: '', account_number: '', ifsc_code: '', upi_id: '' },
  value_propositions: [], terms_and_conditions: [],
  quotation_validity: '30 Days', profile_system: '',
  gst_percent_default: 18, cgst_percent: 9, sgst_percent: 9,
};

function CompanySettings() {
  const [s, setS] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/company-settings');
      setS({ ...initialState, ...data, bank: { ...initialState.bank, ...(data.bank || {}) } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/admin/company-settings', s);
      setS({ ...initialState, ...data, bank: { ...initialState.bank, ...(data.bank || {}) } });
      toast.success('Company settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await api.post('/api/admin/company-settings/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setS((prev) => ({ ...prev, logo_url: data.logo_url }));
      toast.success('Logo uploaded — remember to Save');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo upload failed');
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const setListItem = (key, idx, val) => {
    setS((prev) => ({ ...prev, [key]: prev[key].map((v, i) => (i === idx ? val : v)) }));
  };
  const addListItem = (key, initial = '') => setS((prev) => ({ ...prev, [key]: [...prev[key], initial] }));
  const removeListItem = (key, idx) => setS((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));

  const setBank = (k, v) => setS((prev) => ({ ...prev, bank: { ...prev.bank, [k]: v } }));

  if (loading) {
    return <Layout><div className="p-8 text-zinc-400">Loading company settings…</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 pb-16" data-testid="company-settings-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Company Settings</h1>
            <p className="text-sm text-zinc-500 mt-1">These values are used by the Quotation PDF export and printed artifacts.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="reload-btn">
              <ArrowClockwise size={16} className="mr-1" /> Reload
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-btn"
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              <FloppyDisk size={16} className="mr-1" /> {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Logo + brand colors */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Brand</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            <div className="lg:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Logo</Label>
              <div className="mt-2 border border-dashed border-zinc-300 rounded p-4 h-40 flex items-center justify-center bg-zinc-50">
                {s.logo_url ? (
                  <img src={s.logo_url} alt="Logo" className="max-h-32 max-w-full object-contain" data-testid="logo-preview" />
                ) : (
                  <span className="text-xs text-zinc-400">No logo uploaded yet</span>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden
                     data-testid="logo-file-input"
                     onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="mt-2 w-full" data-testid="upload-logo-btn">
                <UploadSimple size={16} className="mr-1" /> {uploading ? 'Uploading…' : 'Upload Logo'}
              </Button>
              <p className="text-[10px] text-zinc-400 mt-1">PNG / JPG / WEBP up to 5 MB.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Primary Color</Label>
              <div className="flex gap-2 mt-2">
                <input type="color" value={s.primary_color} onChange={(e) => setS({ ...s, primary_color: e.target.value })}
                       className="h-10 w-16 border border-zinc-300 rounded" data-testid="primary-color-picker" />
                <Input value={s.primary_color} onChange={(e) => setS({ ...s, primary_color: e.target.value })}
                       className="flex-1" data-testid="primary-color-input" />
              </div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mt-4 block">Secondary Color</Label>
              <div className="flex gap-2 mt-2">
                <input type="color" value={s.secondary_color} onChange={(e) => setS({ ...s, secondary_color: e.target.value })}
                       className="h-10 w-16 border border-zinc-300 rounded" data-testid="secondary-color-picker" />
                <Input value={s.secondary_color} onChange={(e) => setS({ ...s, secondary_color: e.target.value })}
                       className="flex-1" data-testid="secondary-color-input" />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Company Name</Label>
                <Input value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })}
                       data-testid="company-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Tagline</Label>
                <Input value={s.tagline} onChange={(e) => setS({ ...s, tagline: e.target.value })}
                       data-testid="tagline-input" />
              </div>
            </div>
          </div>
        </CardContent></Card>

        {/* Contact + Legal */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Contact & Legal</h2>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Address</Label>
            <Textarea value={s.address} onChange={(e) => setS({ ...s, address: e.target.value })}
                      rows={2} data-testid="address-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
              <Input value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} data-testid="email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">GSTIN</Label>
              <Input value={s.gstin} onChange={(e) => setS({ ...s, gstin: e.target.value })} data-testid="gstin-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">ISO Certification</Label>
              <Input value={s.iso_certification} onChange={(e) => setS({ ...s, iso_certification: e.target.value })}
                     data-testid="iso-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">ISO Cert Number</Label>
              <Input value={s.iso_cert_number} onChange={(e) => setS({ ...s, iso_cert_number: e.target.value })}
                     data-testid="iso-num-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center justify-between">
              <span>Phone Numbers</span>
              <Button size="sm" variant="ghost" onClick={() => addListItem('phones', '')}
                      data-testid="add-phone-btn"><Plus size={14} /> Add</Button>
            </Label>
            <div className="space-y-2 mt-1">
              {s.phones.map((ph, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={ph} onChange={(e) => setListItem('phones', i, e.target.value)}
                         data-testid={`phone-input-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => removeListItem('phones', i)}
                          data-testid={`remove-phone-${i}`}><Trash size={16} /></Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent></Card>

        {/* Quotation defaults */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Quotation Defaults</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Validity</Label>
              <Input value={s.quotation_validity} onChange={(e) => setS({ ...s, quotation_validity: e.target.value })}
                     data-testid="validity-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Profile System</Label>
              <Input value={s.profile_system} onChange={(e) => setS({ ...s, profile_system: e.target.value })}
                     data-testid="profile-system-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">GST % Default</Label>
              <Input type="number" step="0.01" value={s.gst_percent_default}
                     onChange={(e) => setS({ ...s, gst_percent_default: parseFloat(e.target.value || 0) })}
                     data-testid="gst-pct-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">CGST %</Label>
              <Input type="number" step="0.01" value={s.cgst_percent}
                     onChange={(e) => setS({ ...s, cgst_percent: parseFloat(e.target.value || 0) })}
                     data-testid="cgst-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">SGST %</Label>
              <Input type="number" step="0.01" value={s.sgst_percent}
                     onChange={(e) => setS({ ...s, sgst_percent: parseFloat(e.target.value || 0) })}
                     data-testid="sgst-input" />
            </div>
          </div>
        </CardContent></Card>

        {/* Bank details */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Bank Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Bank Name</Label>
              <Input value={s.bank.bank_name} onChange={(e) => setBank('bank_name', e.target.value)}
                     data-testid="bank-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Branch</Label>
              <Input value={s.bank.branch} onChange={(e) => setBank('branch', e.target.value)}
                     data-testid="bank-branch-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Account Number</Label>
              <Input value={s.bank.account_number} onChange={(e) => setBank('account_number', e.target.value)}
                     data-testid="bank-acc-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">IFSC</Label>
              <Input value={s.bank.ifsc_code} onChange={(e) => setBank('ifsc_code', e.target.value)}
                     data-testid="bank-ifsc-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">UPI</Label>
              <Input value={s.bank.upi_id} onChange={(e) => setBank('upi_id', e.target.value)}
                     data-testid="bank-upi-input" />
            </div>
          </div>
        </CardContent></Card>

        {/* Value propositions */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Value Propositions</h2>
            <Button size="sm" variant="outline" onClick={() => addListItem('value_propositions', '')}
                    data-testid="add-vp-btn"><Plus size={14} /> Add</Button>
          </div>
          <div className="space-y-2">
            {s.value_propositions.map((v, i) => (
              <div key={i} className="flex gap-2">
                <Input value={v} onChange={(e) => setListItem('value_propositions', i, e.target.value)}
                       data-testid={`vp-input-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeListItem('value_propositions', i)}
                        data-testid={`remove-vp-${i}`}><Trash size={16} /></Button>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* Terms and conditions */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-700">Terms & Conditions</h2>
            <Button size="sm" variant="outline" onClick={() => addListItem('terms_and_conditions', '')}
                    data-testid="add-term-btn"><Plus size={14} /> Add</Button>
          </div>
          <div className="space-y-2">
            {s.terms_and_conditions.map((t, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="text-xs font-mono text-zinc-500 pt-2 w-8 text-right">{String(i + 1).padStart(2, '0')}.</span>
                <Textarea value={t} onChange={(e) => setListItem('terms_and_conditions', i, e.target.value)}
                          rows={2} className="flex-1" data-testid={`term-input-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeListItem('terms_and_conditions', i)}
                        data-testid={`remove-term-${i}`}><Trash size={16} /></Button>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* Sticky save reminder */}
        <div className="flex justify-end sticky bottom-0 py-3 bg-white border-t border-zinc-200">
          <Button onClick={save} disabled={saving} data-testid="save-btn-bottom"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            <FloppyDisk size={16} className="mr-1" /> {saving ? 'Saving…' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}

export default CompanySettings;
export { CompanySettings };
